import React from "react";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import RemoveIcon from "@mui/icons-material/Remove";
import CheckIcon from "@mui/icons-material/Check";
import { Link } from "react-router-dom";
import { ORDER_STATUS } from "./constants";
import i18n from "../i18n";
import { capitalizeFirstLetter } from "../utils";
import { dateColumnDef } from "./dateTimeColumnDef";

export const orderNumber = (t, width = 140) => {
  return {
    field: "trackingCode",
    headerName: `${t("ordersTableHeaders.orderNumber")}`,
    width: width,
    align: "left",
    headerAlign: "left",
    renderCell: ({ value }) => {
      const trackingCode = value;
      return (
        <Link
          to={`/orders/${trackingCode}/`}
          className={"text-black"}
          target="_blank"
          rel="noopener noreferrer"
        >
          {trackingCode}
        </Link>
      );
    },
  };
};
export const printIcon = (t, width = 85, handlePrint) => {
  return {
    field: `${t("ordersTableHeaders.actions")}`,
    align: "left",
    headerAlign: "left",
    width: width,
    type: "actions",
    getActions: (params) => {
      if (String(params.id).toLowerCase().includes("auto-generated-row")) {
        return [];
      }
      const registerNumber = params.row?.registerNumber;
      return [
        <GridActionsCellItem
          icon={<PrintOutlinedIcon />}
          onClick={() => {
            if (params.id && registerNumber !== null) {
              handlePrint(params.id);
            }
          }}
          label={t("dataGridTable.print")}
          disabled={registerNumber === null}
        />,
      ];
    },
  };
};
export const description = (t) => {
  return {
    field: "description",
    headerName: `${t("ordersTableHeaders.description")}`,
    width: 250,
    align: "left",
    headerAlign: "left",
  };
};
export const recipient = (t, width = 200) => {
  return {
    field: "recipientName",
    headerName: `${t("ordersTableHeaders.recipient")}`,
    width: width,
    headerAlign: "left",
  };
};
export const recipientPhone = (t) => {
  return {
    field: "recipientPhone",
    headerName: `${t("ordersTableHeaders.recipientPhone")}`,
    width: 150,
    headerAlign: "left",
  };
};
export const deliveryCity = (t, width = 150) => {
  return {
    field: "deliveryCity",
    headerName: `${t("ordersTableHeaders.deliveryCity")}`,
    width: width,
    headerAlign: "left",
  };
};
export const deliveryAddress = (t, width = 350) => {
  return {
    field: "deliveryAddress",
    headerName: `${t("ordersTableHeaders.deliveryAddress")}`,
    width: width,
    headerAlign: "left",
  };
};
export const estimatedCost = (t) => {
  return {
    field: "estimatedCost",
    headerName: `${t("ordersTableHeaders.estimatedCost")}`,
    width: 120,
    align: "right",
    headerAlign: "right",
    type: "number",
  };
};
export const cod = (t) => {
  return {
    field: "cod",
    headerName: `${t("ordersTableHeaders.cod")}`,
    width: 80,
    align: "right",
    headerAlign: "right",
    type: "number",
    renderCell: ({ value }) => {
      const codCost = value;
      return codCost ? codCost : <RemoveIcon className={"pt-3"} />;
    },
  };
};
export const codByRecipient = (t) => {
  return {
    field: "codByRecipient",
    headerName: `${t("ordersTableHeaders.codByRecipient")}`,
    width: 120,
    align: "right",
    headerAlign: "right",
    type: "boolean",
    renderCell: ({ row, value }) => {
      const isCodByRecipient = value;
      const codCost = row.cod;

      if (codCost === 0) {
        return <RemoveIcon />;
      } else {
        return isCodByRecipient
          ? `${t("ordersTableHeaders.recipient")}`
          : `${t("ordersTableHeaders.sender")}`;
      }
    },
  };
};
export const codReturnDate = (t) => {
  return {
    field: "codReturnDate",
    headerName: `${t("ordersTableHeaders.codReturnDate")}`,
    width: 180,
    ...dateColumnDef,
  }
};
export const otherCosts = (t) => {
  return {
    field: "addServicesCost",
    headerName: `${t("ordersTableHeaders.otherCosts")}`,
    width: 120,
    align: "right",
    headerAlign: "right",
    type: "number",
  };
};

export const registerNumber = (t, width = 150, hasLink = true) => {
  return {
    field: "registerNumber",
    headerName: `${t("ordersTableHeaders.registerNumber")}`,
    width: width,
    headerAlign: "left",
    renderCell: ({ id, value }) => {
      const registryId = id;
      const registryNumber = value;
      return hasLink ? (
        <Link to={`/registers/${registryId}/`} className={"text-black"}>
          {registryNumber}
        </Link>
      ) : (
        <span>{registryNumber}</span>
      );
    },
  };
};
export const deliveryCost = (t, width = 100) => {
  return {
    field: "deliveryCost",
    headerName: `${t("ordersTableHeaders.deliveryCost")}`,
    width: width,
    align: "right",
    headerAlign: "right",
    type: "number",
  };
};

export const weight = (t, width = 80) => {
  return {
    field: "weight",
    headerName: `${t("ordersTableHeaders.weight")}`,
    width: width,
    type: "number",
    headerAlign: "right",
    align: "right",
  };
};

export const customTag = (t, width = 150) => {
  return {
    field: "customTag",
    headerName: `${t("orderCreation.parcelAttribute")}`,
    width: width,
    headerAlign: "left",
  };
};

export const departureCity = (t, width = 150) => {
  return {
    field: "departureCity",
    headerName: `${t("ordersTableHeaders.departureCity")}`,
    width: width,
    headerAlign: "left",
  };
};
export const departureAddress = (t, width = 350) => {
  return {
    field: "departureAddress",
    headerName: `${t("ordersTableHeaders.departureAddress")}`,
    width: width,
    headerAlign: "left",
  };
};

export const internationalTrackingNumber = (t, width = 200) => {
  return {
    field: "internationalTrackingNumber",
    headerName: `${t("ordersTableHeaders.internationalTrackingNumber")}`,
    width: width,
    headerAlign: "left",
  };
};

export const shipmentStatus = (t, formatOrderStatus, width = 220) => {
  return {
    field: "shipmentStatus",
    headerName: `${t("ordersTableHeaders.status")}`,
    width: width,
    align: "left",
    headerAlign: "left",
    valueFormatter: (value) => capitalizeFirstLetter(value, i18n.language),
    valueGetter: (value) => formatOrderStatus(value).toLocaleLowerCase(i18n.language),
  };
};
export const is_return = (t, width = 100) => {
  return {
    field: "return", // или "is_return" — см. как приходит с бэкенда
    headerName: t("ordersTableHeaders.returnDelivery"), // или любое другое название
    width: width,
    align: "left",
    headerAlign: "left",
    renderCell: ({ value }) => {
      // Если у вас просто true/false, можно отображать как "Yes"/"No" или иконки
      if (value === true) {
        return "Yes";
      } else if (value === false) {
        return "No";
      } else {
        // Если вдруг нет значения (null / undefined)
        return <RemoveIcon />;
      }
    },
  };
};

export const withPlaces = (t) => {
  return {
    field: "withPlaces",
    headerName: `${t("ordersTableHeaders.withPlaces")}`,
    width: 80,
    align: "right",
    headerAlign: "right",
    type: "boolean",
    renderCell: ({ value, params }) => {
      return value === true ? <CheckIcon /> : <RemoveIcon />;
    },
  };
};

export const places = (t) => {
  return {
    field: "places",
    headerName: `${t("ordersTableHeaders.places")}`,
    width: 80,
    align: "right",
    headerAlign: "right",
    type: "number",
    renderCell: ({ value }) => {
      return Array.isArray(value) ? value.length : <RemoveIcon className="pt-3" />;
    },
  };
};

export const returnParcelToSender = (t) => {
  return {
    field: "returnParcelToSender",
    headerName: `${t("ordersTableHeaders.returnParcelToSender")}`,
    width: 150,
    align: "right",
    headerAlign: "right",
    renderCell: ({ value }) => {
      return value === true ? <CheckIcon /> : <RemoveIcon />;
    },
  };
};

export const returnParcelToSenderTrackNumber = (t, width = 250) => {
  return {
    field: "returnParcelToSenderTrackNumber",
    headerName: `${t("ordersTableHeaders.returnParcelToSenderTrackNumber")}`,
    width: width,
    align: "left",
    headerAlign: "left",
    renderCell: ({ value, row }) => {
      if (row.returnParcelToSender) {
        if (value) {
          return value;
        }
        return <RemoveIcon />;
      }
      return "";
    },
  };
};

export const returnParcelToSenderStatus = (t, formatOrderStatus, width = 250) => {
  return {
    field: "returnParcelToSenderStatus",
    headerName: `${t("orderTracking.returnOnDeliveryStatus")}`,
    width: width,
    align: "left",
    headerAlign: "left",
    valueFormatter: (value) => {
      if (value === ORDER_STATUS.DRAFT) {
        return "";
      }

      return capitalizeFirstLetter(value, i18n.language);
    },
    valueGetter: (value) => {
      if (value === ORDER_STATUS.DRAFT) {
        return "";
      }

      return value && formatOrderStatus(value).toLocaleLowerCase(i18n.language);
    },
  };
};

export const totalServicesAmountBySender = (t, width = 330) => {
  return {
    field: "totalServicesAmountBySender",
    headerName: `${t("ordersTableHeaders.totalServicesAmountBySender")}`,
    width: width,
    align: "left",
    headerAlign: "left",
  };
};

export const totalServicesAmountByRecipient = (t, width = 300) => {
  return {
    field: "totalServicesAmountByRecipient",
    headerName: `${t("ordersTableHeaders.totalServicesAmountByRecipient")}`,
    width: width,
    align: "left",
    headerAlign: "left",
  };
};
